import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Button,
  DataTable,
  SearchWithFilter,
  type DataTableColumn,
  type DataTableRow,
  type FilterSelection,
} from '@enamelai/design-system'
import { FILTER_SHOW_ALL } from '@constants/Notifications'
import { useReportInPlaceExpandOptional } from '@components/pages/Reports/shared/ReportInPlaceExpandContext'
import { toSentenceCase } from '@utils/textCase.util'

const FULL_VIEW_PAGE_SIZE_OPTIONS = [10, 25, 50, 100]

export type ReportDataTableColumn<TRow> = {
  key: keyof TRow | string
  label: string
  headerClassName?: string
  cellClassName?: string
  render?: (row: TRow) => React.ReactNode
  sortable?: boolean
  sortValue?: (row: TRow) => string | number
}

export type ReportDataTableFullViewSearchConfig<TRow> = {
  filters?: Record<string, string[]>
  filterLabels?: Record<string, string>
  placeholder?: string
  searchIcon?: string
  filterIcon?: string
  filterLabel?: string
  className?: string
  filterCategoryToRowKey?: Record<string, keyof TRow | string>
  searchTextFromRow?: (row: TRow) => string
}

type ReportDataTableProps<TRow> = {
  columns: ReportDataTableColumn<TRow>[]
  rows: TRow[]
  rowKey: (row: TRow, index: number) => string
  pageSizeOptions?: number[]
  initialPageSize?: number
  className?: string
  footerEndSlot?: React.ReactNode
  showFullViewButton?: boolean
  showExitFullViewButton?: boolean
  showPagination?: boolean
  /** When set with fullViewSectionId, card view shows this many rows (defaults to initialPageSize). */
  previewRowLimit?: number
  onFullViewClick?: () => void
  fullViewSectionId?: string
  enableSorting?: boolean
  sortIndicatorVariant?: 'default' | 'dashboard'
  fullViewSearchWithFilter?: true | ReportDataTableFullViewSearchConfig<TRow>
}

function buildInitialFilterSelection(
  filters: Record<string, string[]>,
): FilterSelection {
  const sel: FilterSelection = {}
  for (const [cat, opts] of Object.entries(filters)) {
    if (opts.includes(FILTER_SHOW_ALL)) sel[cat] = [FILTER_SHOW_ALL]
  }
  return sel
}

function defaultSearchTextFromRow<TRow>(
  row: TRow,
  columns: ReportDataTableColumn<TRow>[],
): string {
  return columns
    .filter((c) => {
      const k = String(c.key)
      return k !== 'actions' && !k.startsWith('__')
    })
    .map((c) => {
      const v = (row as Record<string, unknown>)[String(c.key)]
      if (v == null) return ''
      return String(v)
    })
    .join(' ')
}

function rowMatchesFullViewFilters<TRow>(
  row: TRow,
  selection: FilterSelection,
  categoryToKey: Record<string, keyof TRow | string> | undefined,
  filterCategories: string[],
): boolean {
  for (const category of filterCategories) {
    const selected = selection[category]
    if (selected == null || selected.length === 0) continue
    if (selected.length === 1 && selected[0] === FILTER_SHOW_ALL) continue
    const key = categoryToKey?.[category] ?? category
    const raw = (row as Record<string, unknown>)[String(key)]
    const val = raw == null ? '' : String(raw)
    const effective = selected.filter((s) => s !== FILTER_SHOW_ALL)
    if (effective.length === 0) continue
    if (!effective.includes(val)) return false
  }
  return true
}

function ReportDataTable<TRow>({
  columns,
  rows,
  rowKey,
  pageSizeOptions = [5, 10, 25],
  initialPageSize = 5,
  className = '',
  footerEndSlot,
  showFullViewButton,
  showExitFullViewButton,
  showPagination,
  previewRowLimit,
  onFullViewClick,
  fullViewSectionId,
  enableSorting = true,
  sortIndicatorVariant: _sortIndicatorVariant = 'dashboard',
  fullViewSearchWithFilter,
}: ReportDataTableProps<TRow>): React.ReactElement {
  void _sortIndicatorVariant
  const expandCtx = useReportInPlaceExpandOptional()
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(initialPageSize)
  const [sort, setSort] = useState<{
    key: string
    dir: 'asc' | 'desc'
  } | null>(null)

  const [fullViewSearchQuery, setFullViewSearchQuery] = useState('')
  const [fullViewFilterSelection, setFullViewFilterSelection] =
    useState<FilterSelection>({})

  const fullViewHandler: (() => void) | undefined =
    onFullViewClick ??
    (fullViewSectionId != null && expandCtx != null
      ? () => {
          expandCtx.expand(fullViewSectionId)
        }
      : undefined)

  const isInPlaceFullView =
    fullViewSectionId != null &&
    expandCtx != null &&
    expandCtx.expandedSectionId === fullViewSectionId

  const useInPlaceFullViewMode = fullViewSectionId != null

  const effectiveShowPagination =
    showPagination ?? (!useInPlaceFullViewMode || isInPlaceFullView)

  const effectiveShowFullViewButton =
    showFullViewButton ?? !useInPlaceFullViewMode

  const effectiveShowExitFullViewButton =
    showExitFullViewButton ?? !useInPlaceFullViewMode

  const effectivePreviewRowLimit =
    previewRowLimit ??
    (useInPlaceFullViewMode && !isInPlaceFullView ? initialPageSize : undefined)

  const tableRows = useMemo(() => {
    if (effectivePreviewRowLimit == null) return rows
    return rows.slice(0, effectivePreviewRowLimit)
  }, [rows, effectivePreviewRowLimit])

  const canShowFullViewButton =
    effectiveShowFullViewButton &&
    (onFullViewClick != null ||
      (fullViewSectionId != null && expandCtx != null))

  const effectivePageSizeOptions = isInPlaceFullView
    ? FULL_VIEW_PAGE_SIZE_OPTIONS
    : pageSizeOptions

  const fullViewAutoFilter = useMemo(() => {
    if (fullViewSearchWithFilter !== true) return null
    const firstDataColumn = columns.find((c) => {
      const k = String(c.key)
      return k !== 'actions' && !k.startsWith('__')
    })
    if (!firstDataColumn) return null
    const categoryId = `__auto_${String(firstDataColumn.key)}`
    const optionSet = new Set<string>()
    for (const row of tableRows) {
      const raw = (row as Record<string, unknown>)[String(firstDataColumn.key)]
      if (raw == null) continue
      const text = String(raw).trim()
      if (text.length > 0) optionSet.add(text)
    }
    const options = Array.from(optionSet).sort((a, b) =>
      a.localeCompare(b, undefined, {
        numeric: true,
        sensitivity: 'base',
      }),
    )
    return {
      categoryId,
      filters:
        options.length > 0
          ? ({ [categoryId]: [FILTER_SHOW_ALL, ...options] } as Record<
              string,
              string[]
            >)
          : ({} as Record<string, string[]>),
      filterLabels: { [categoryId]: firstDataColumn.label } as Record<
        string,
        string
      >,
      filterCategoryToRowKey: {
        [categoryId]: firstDataColumn.key,
      } as Record<string, keyof TRow | string>,
      showFilter: options.length > 0,
    }
  }, [fullViewSearchWithFilter, columns, tableRows])

  const fullViewSearchResolved = useMemo(() => {
    if (fullViewSearchWithFilter == null) return null
    if (fullViewSearchWithFilter === true) {
      return {
        filters:
          fullViewAutoFilter?.filters ?? ({} as Record<string, string[]>),
        showFilter: fullViewAutoFilter?.showFilter ?? false,
        filterLabels: fullViewAutoFilter?.filterLabels,
        filterCategoryToRowKey: fullViewAutoFilter?.filterCategoryToRowKey,
        searchTextFromRow: undefined as ((row: TRow) => string) | undefined,
        placeholder: 'Search',
        searchIcon: 'mgc_search_2_line',
        filterIcon: 'mgc_settings_2_line',
        filterLabel: '',
        className: '',
      }
    }
    const f: ReportDataTableFullViewSearchConfig<TRow> =
      fullViewSearchWithFilter
    const filters = f.filters ?? {}
    return {
      filters,
      showFilter: Object.keys(filters).length > 0,
      filterLabels: f.filterLabels,
      filterCategoryToRowKey: f.filterCategoryToRowKey,
      searchTextFromRow: f.searchTextFromRow,
      placeholder: f.placeholder ?? 'Search',
      searchIcon: f.searchIcon ?? 'mgc_search_2_line',
      filterIcon: f.filterIcon ?? 'mgc_settings_2_line',
      filterLabel: f.filterLabel ?? '',
      className: f.className ?? '',
    }
  }, [fullViewSearchWithFilter, fullViewAutoFilter])

  const wasInPlaceFullViewRef = useRef(false)
  useEffect(() => {
    const justEntered =
      isInPlaceFullView &&
      !wasInPlaceFullViewRef.current &&
      fullViewSearchWithFilter != null
    wasInPlaceFullViewRef.current = isInPlaceFullView
    if (!justEntered) return
    setFullViewSearchQuery('')
    const filters =
      fullViewSearchWithFilter === true
        ? (fullViewAutoFilter?.filters ?? {})
        : (fullViewSearchWithFilter.filters ?? {})
    setFullViewFilterSelection(buildInitialFilterSelection(filters))
    setCurrentPage(1)
  }, [isInPlaceFullView, fullViewSearchWithFilter, fullViewAutoFilter])

  useEffect(() => {
    if (isInPlaceFullView && !effectivePageSizeOptions.includes(pageSize)) {
      setPageSize(FULL_VIEW_PAGE_SIZE_OPTIONS[0])
      setCurrentPage(1)
    }
  }, [isInPlaceFullView, pageSize, effectivePageSizeOptions])

  const filterCategoryIds = useMemo(
    () =>
      fullViewSearchResolved != null
        ? Object.keys(fullViewSearchResolved.filters)
        : [],
    [fullViewSearchResolved],
  )

  const rowsAfterFullViewSearch = useMemo(() => {
    if (!isInPlaceFullView || fullViewSearchResolved == null) return tableRows
    const q = fullViewSearchQuery.trim().toLowerCase()
    let out = tableRows
    if (q) {
      const textFn =
        fullViewSearchResolved.searchTextFromRow ??
        ((r: TRow) => defaultSearchTextFromRow(r, columns))
      out = out.filter((r) => textFn(r).toLowerCase().includes(q))
    }
    if (filterCategoryIds.length > 0) {
      out = out.filter((r) =>
        rowMatchesFullViewFilters(
          r,
          fullViewFilterSelection,
          fullViewSearchResolved.filterCategoryToRowKey,
          filterCategoryIds,
        ),
      )
    }
    return out
  }, [
    tableRows,
    isInPlaceFullView,
    fullViewSearchResolved,
    fullViewSearchQuery,
    fullViewFilterSelection,
    filterCategoryIds,
    columns,
  ])

  const sortedRows = useMemo(() => {
    if (!enableSorting || sort == null) return rowsAfterFullViewSearch
    const col = columns.find((c) => String(c.key) === sort.key)
    if (!col || col.sortable === false) return rowsAfterFullViewSearch

    const getVal = (row: TRow): string | number => {
      if (col.sortValue) return col.sortValue(row)
      const v = (row as Record<string, unknown>)[String(col.key)]
      if (typeof v === 'number') return v
      if (v == null) return ''
      return String(v)
    }

    const copy = [...rowsAfterFullViewSearch]
    copy.sort((a, b) => {
      const va = getVal(a)
      const vb = getVal(b)
      let cmp = 0
      if (typeof va === 'number' && typeof vb === 'number') {
        cmp = va - vb
      } else {
        cmp = String(va).localeCompare(String(vb), undefined, {
          numeric: true,
          sensitivity: 'base',
        })
      }
      return sort.dir === 'asc' ? cmp : -cmp
    })
    return copy
  }, [rowsAfterFullViewSearch, sort, columns, enableSorting])

  const pagedRows = useMemo(() => {
    if (!effectiveShowPagination) return sortedRows
    const start = (currentPage - 1) * pageSize
    return sortedRows.slice(start, start + pageSize)
  }, [currentPage, pageSize, effectiveShowPagination, sortedRows])

  const handleSortHeaderClick = (column: ReportDataTableColumn<TRow>): void => {
    if (!enableSorting || column.sortable === false) return
    const key = String(column.key)
    setCurrentPage(1)
    setSort((prev) => {
      if (prev?.key === key) {
        return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      }
      return { key, dir: 'asc' }
    })
  }

  const handlePageChange = (page: number): void => setCurrentPage(page)

  const handlePageSizeChange = (newPageSize: number): void => {
    setPageSize(newPageSize)
    setCurrentPage(1)
  }

  const handleFullViewSearchChange = useCallback((value: string): void => {
    setFullViewSearchQuery(value)
    setCurrentPage(1)
  }, [])

  const handleFullViewFilterChange = useCallback((next: FilterSelection) => {
    setFullViewFilterSelection(next)
    setCurrentPage(1)
  }, [])

  const hasFooterEnd =
    footerEndSlot != null ||
    canShowFullViewButton ||
    (isInPlaceFullView && effectiveShowExitFullViewButton)

  const showFullViewSearchBar =
    isInPlaceFullView && fullViewSearchResolved != null

  type ReportDsRow = DataTableRow & { __sourceIndex: number }

  const dsRows = useMemo<ReportDsRow[]>(
    () =>
      pagedRows.map((row, index) => {
        const sourceIndex = sortedRows.indexOf(row)
        const dsRow: ReportDsRow = {
          id: rowKey(row, sourceIndex >= 0 ? sourceIndex : index),
          __sourceIndex: sourceIndex >= 0 ? sourceIndex : index,
        }
        columns.forEach((column) => {
          const columnKey = String(column.key)
          dsRow[columnKey] = column.render
            ? column.render(row)
            : String((row as Record<string, unknown>)[columnKey] ?? '')
        })
        return dsRow
      }),
    [columns, pagedRows, rowKey, sortedRows],
  )

  const dsColumns = useMemo<DataTableColumn<ReportDsRow>[]>(
    () =>
      columns.map((column) => ({
        id: String(column.key),
        label: toSentenceCase(column.label),
        sortable: enableSorting && column.sortable !== false,
      })),
    [columns, enableSorting],
  )

  return (
    <div className={`reports-data-table ${className}`}>
      {showFullViewSearchBar ? (
        <div className="reports-data-table__full-view-search">
          <SearchWithFilter
            className={[
              'reports-data-table__full-view-swf',
              fullViewSearchResolved.className,
            ]
              .filter(Boolean)
              .join(' ')}
            placeholder={fullViewSearchResolved.placeholder}
            value={fullViewSearchQuery}
            onChange={handleFullViewSearchChange}
            filters={fullViewSearchResolved.filters}
            filterLabels={fullViewSearchResolved.filterLabels}
            onFilterChange={handleFullViewFilterChange}
            selectedFilters={fullViewFilterSelection}
            showFilter={fullViewSearchResolved.showFilter}
          />
        </div>
      ) : null}
      <DataTable
        columns={dsColumns}
        rows={dsRows}
        showActions={false}
        sortColumnId={sort?.key}
        sortDirection={sort?.dir}
        onSort={(columnId) => {
          const column = columns.find(
            (candidate) => String(candidate.key) === columnId,
          )
          if (column) handleSortHeaderClick(column)
        }}
        pagination={
          effectiveShowPagination
            ? {
                currentPage,
                totalPages: Math.max(
                  1,
                  Math.ceil(sortedRows.length / pageSize),
                ),
                pageSize,
                pageSizeOptions: effectivePageSizeOptions,
                onPageChange: handlePageChange,
                onPageSizeChange: handlePageSizeChange,
              }
            : undefined
        }
        emptyState={{ title: 'No report data available' }}
        className="reports-data-table__table"
      />
      {hasFooterEnd ? (
        <div className="reports-data-table__footer-row">
          {hasFooterEnd ? (
            <div className="reports-data-table__footer-row-end">
              {footerEndSlot}
              {isInPlaceFullView && effectiveShowExitFullViewButton ? (
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={() => expandCtx?.exit()}
                >
                  Exit full view
                </Button>
              ) : canShowFullViewButton && fullViewHandler != null ? (
                <Button
                  type="button"
                  size="sm"
                  variant="primary"
                  onClick={fullViewHandler}
                >
                  Full View
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

export default ReportDataTable
